import { ConvexError } from "convex/values";
import type { MutationCtx, QueryCtx } from "../../_generated/server";
import type { Doc, Id } from "../../_generated/dataModel";
import type { UserContext } from "../../system/context";
import type { CustomFieldValues } from "../custom_fields";
import { hasPermissionInContext } from "../../system/permissions/context_check";
import { peopleText } from "../custom_fields/person";
import { formatAddress, readAddress } from "../custom_fields/address";

/** Máximo de colecciones propias por organización (sin contar las privadas); igual al tope del manifiesto. */
export const MAX_COLLECTIONS = 80;
/** Máximo de colecciones privadas por usuario. */
export const MAX_PRIVATE_COLLECTIONS = 10;

/** Quien pregunta por una colección: rol, y usuario para las privadas. */
export type CollectionViewer = Pick<UserContext, "isSuperAdmin" | "role" | "roleId"> & {
  userId?: Id<"users"> | null;
};

type CollectionAccessDoc = Pick<Doc<"collections">, "allowedRoleIds" | "visibility" | "ownerId"> & {
  internalControl?: { canView?: ReadonlyArray<string>; canEdit?: ReadonlyArray<string> };
};

/** Nivel de acceso a una colección privada: dueño, edita, ve, o ninguno. */
export type PrivateAccess = "owner" | "edit" | "view" | null;

export const isPrivateCollection = (collection: Pick<Doc<"collections">, "visibility">) =>
  collection.visibility === "private";

/**
 * Acceso a una colección privada. Privada es privada: ni el admin de la
 * empresa ni el super admin pasan; solo el dueño y las personas con quienes
 * la compartió (`internalControl.canView` / `canEdit`).
 */
export function privateAccess(viewer: Pick<CollectionViewer, "userId">, collection: CollectionAccessDoc): PrivateAccess {
  const userId = viewer.userId;
  if (!userId) return null;
  if (collection.ownerId === userId) return "owner";
  if (collection.internalControl?.canEdit?.includes(userId)) return "edit";
  if (collection.internalControl?.canView?.includes(userId)) return "view";
  return null;
}

/**
 * ¿La lista de roles deja pasar al usuario? Vacía = todos. Super admin y
 * admins de empresa siempre pasan.
 */
export function rolesAllow(
  viewer: Pick<UserContext, "isSuperAdmin" | "role" | "roleId">,
  allowedRoleIds: ReadonlyArray<Id<"roles">> | undefined,
): boolean {
  if (viewer.isSuperAdmin) return true;
  if (viewer.role?.type === "company_admin") return true;
  const allowed = allowedRoleIds ?? [];
  if (allowed.length === 0) return true;
  return !!viewer.roleId && allowed.includes(viewer.roleId);
}

/**
 * ¿El usuario puede ver la colección? Además del módulo `collection_records`
 * (lo aplica quien llama): en una privada, ser el dueño o tenerla compartida;
 * en una de empresa, que su rol esté en `allowedRoleIds` (vacía = todos;
 * super admin y admins de empresa siempre pasan).
 */
export function canAccessCollection(viewer: CollectionViewer, collection: CollectionAccessDoc): boolean {
  if (isPrivateCollection(collection)) return privateAccess(viewer, collection) !== null;
  return rolesAllow(viewer, collection.allowedRoleIds);
}

export type RecordAction = "create" | "update" | "delete" | "pdf";

/**
 * El rol del usuario puede crear / editar / borrar registros en la colección
 * según `recordRoleIds` (acciones predeterminadas). No mira el permiso del
 * módulo `collection_records`: se combina con él. Admins siempre pueden.
 */
export function canRecordAction(
  userContext: CollectionViewer & Partial<Pick<UserContext, "permissions">>,
  collection: CollectionAccessDoc & Pick<Doc<"collections">, "recordRoleIds"> & Partial<Pick<Doc<"collections">, "config">>,
  action: RecordAction,
): boolean {
  // Configuración: su registro no se borra («Restablecer») y lo editan quien
  // configura (`custom_fields`) o los roles de `recordRoleIds.update`. El alta
  // la hace el sistema al primer uso, con la misma regla que editar.
  if (collection.config && action !== "pdf") {
    if (action === "delete") return false;
    if (userContext.isSuperAdmin || userContext.role?.type === "company_admin") return true;
    if (userContext.permissions && hasPermissionInContext({ isSuperAdmin: userContext.isSuperAdmin, permissions: userContext.permissions }, "custom_fields", "update")) {
      return true;
    }
    return !!userContext.roleId && (collection.recordRoleIds?.update ?? []).includes(userContext.roleId);
  }
  // Privada: el dueño y quien la edita hacen todo; quien solo ve, el PDF.
  if (isPrivateCollection(collection)) {
    const access = privateAccess(userContext, collection);
    return action === "pdf" ? access !== null : access === "owner" || access === "edit";
  }
  if (userContext.isSuperAdmin) return true;
  if (userContext.role?.type === "company_admin") return true;
  const allowed = collection.recordRoleIds?.[action] ?? [];
  if (allowed.length === 0) return true;
  return !!userContext.roleId && allowed.includes(userContext.roleId);
}

const RECORD_ACTION_DENIED: Record<RecordAction, string> = {
  create: "Tu rol no puede crear registros en esta colección",
  update: "Tu rol no puede editar registros de esta colección",
  delete: "Tu rol no puede borrar registros de esta colección",
  pdf: "Tu rol no puede descargar el PDF de los registros de esta colección",
};

export function recordActionDeniedMessage(action: RecordAction): string {
  return RECORD_ACTION_DENIED[action];
}

/**
 * Colección vigente de la empresa del usuario a la que tiene acceso, o null.
 * Lectura directa (las colecciones no son un módulo propio).
 */
export async function getAccessibleCollection(
  ctx: QueryCtx,
  userContext: UserContext,
  collectionId: string,
): Promise<Doc<"collections"> | null> {
  const id = ctx.db.normalizeId("collections", collectionId);
  if (!id) return null;
  const collection = await ctx.db.get(id);
  if (
    !collection ||
    collection.internalControl.companyId !== userContext.companyId ||
    collection.internalControl.state === "deleted"
  ) {
    return null;
  }
  return canAccessCollection(userContext, collection) ? collection : null;
}

/** Colecciones vigentes de la empresa, en orden. */
export async function listCompanyCollections(
  ctx: QueryCtx,
  companyId: Id<"companies">,
): Promise<Doc<"collections">[]> {
  const rows = await ctx.db
    .query("collections")
    .withIndex("by_company_and_order", (q) =>
      q.eq("internalControl.companyId", companyId),
    )
    .take(MAX_COLLECTIONS * 2);
  return rows.filter((c) => c.internalControl.state !== "deleted");
}

/**
 * Texto para la búsqueda de la tabla: los valores de texto, selección,
 * contacto y número. Las relaciones (ids) y casillas no aportan.
 */
/** Valores de una lista libre (JSON string) separados por coma. */
function chipsText(value: unknown): string {
  if (typeof value !== "string" || !value) return "";
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String).join(", ") : value;
  } catch {
    return value;
  }
}

/** Dirección (JSON string): dirección escrita, municipio, departamento y país. */
function addressSearchText(value: unknown): string {
  const address = readAddress(value);
  return address ? formatAddress(address) : "";
}

export function buildSearchText(
  defs: ReadonlyArray<Pick<Doc<"custom_fields">, "key" | "type">>,
  values: CustomFieldValues,
): string {
  const searchable = new Set([
    "text",
    "textarea",
    "select",
    "email",
    "phone",
    "url",
    "number",
    "currency",
  ]);
  return defs
    .filter((def) => searchable.has(def.type) || def.type === "chips" || def.type === "person" || def.type === "address")
    .map((def) =>
      def.type === "chips"
        ? chipsText(values[def.key])
        : def.type === "address"
          ? addressSearchText(values[def.key])
        : def.type === "person"
          ? peopleText(values[def.key])
          : values[def.key],
    )
    .filter((v) => v !== undefined && v !== null && v !== "")
    .map((v) => String(v))
    .join(" ")
    .toLowerCase()
    .slice(0, 4000);
}

/**
 * Configurar colecciones (y sus campos y automatizaciones) exige el módulo
 * `custom_fields` (Configuración → Colecciones).
 */
/**
 * Permiso de configuración (`custom_fields`) con el contexto del usuario que
 * llama: el de la sesión (UI) o el del token (MCP de empresa). No lee la
 * sesión, así las mismas funciones `…Core` sirven a los dos.
 */
export async function requireConfigPermission(
  userContext: Pick<UserContext, "isSuperAdmin" | "permissions">,
  action: "create" | "update" | "delete",
): Promise<void> {
  if (!hasPermissionInContext(userContext, "custom_fields", action)) {
    throw new ConvexError(`Permiso requerido: custom_fields:${action}`);
  }
}

/**
 * Colección vigente de la empresa para configurarla (sin chequeo de roles).
 * Una privada solo la configura su dueño: para los demás no existe.
 */
export async function getCompanyCollection(
  ctx: QueryCtx,
  companyId: Id<"companies">,
  id: Id<"collections">,
  viewer: Pick<UserContext, "userId">,
): Promise<Doc<"collections">> {
  const collection = await ctx.db.get(id);
  if (
    !collection ||
    collection.internalControl.companyId !== companyId ||
    collection.internalControl.state === "deleted" ||
    (isPrivateCollection(collection) && privateAccess(viewer, collection) !== "owner")
  ) {
    throw new ConvexError("Colección no encontrada");
  }
  return collection;
}

/**
 * Colección a configurar y permiso para hacerlo: en una privada basta con ser
 * su dueño; en una de la empresa, el permiso de configuración
 * (`custom_fields`). Para lo básico (datos, campos, vistas y formulario).
 */
export async function requireCollectionConfig(
  ctx: QueryCtx,
  userContext: UserContext,
  id: Id<"collections">,
  action: "create" | "update" | "delete",
): Promise<Doc<"collections">> {
  const collection = await getCompanyCollection(ctx, userContext.companyId, id, userContext);
  if (!(isPrivateCollection(collection) && collection.ownerId === userContext.userId)) {
    await requireConfigPermission(userContext, action);
  }
  return collection;
}

/**
 * ¿`from` puede referenciar datos de `target` (relación, resumen, líneas)?
 * Una privada solo la referencia otra privada del mismo dueño: si no, sus
 * etiquetas, resúmenes y búsquedas llegarían a quien ve `from`.
 */
export function canReference(
  from: Pick<Doc<"collections">, "visibility" | "ownerId">,
  target: Pick<Doc<"collections">, "visibility" | "ownerId">,
): boolean {
  if (!isPrivateCollection(target)) return true;
  return isPrivateCollection(from) && !!from.ownerId && from.ownerId === target.ownerId;
}

export const PRIVATE_REFERENCE_ERROR =
  "Una colección privada solo se puede usar desde otra colección privada del mismo dueño";

/** ¿El usuario configura esta colección? Las privadas, solo su dueño. */
export function isConfigurableBy(viewer: Pick<CollectionViewer, "userId">, collection: Doc<"collections">): boolean {
  return !isPrivateCollection(collection) || privateAccess(viewer, collection) === "owner";
}

/** ¿La colección aparece para este usuario en listas de configuración? */
export function isListedFor(viewer: Pick<CollectionViewer, "userId">, collection: Doc<"collections">): boolean {
  return !isPrivateCollection(collection) || privateAccess(viewer, collection) !== null;
}
